import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableRowActionsCell } from 'altus-ui-components';
import { Grid, Typography, IconButton } from '@material-ui/core';
import withStyles from '@material-ui/styles/withStyles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import WarningIcon from '@material-ui/icons/Warning';

import routePaths from 'app/routePaths';
import { EMPTY_LIST } from 'app/app.constants';
import {
  RULE_TYPES_ENUM,
  getRuleTypeString,
  INTEGRITY_TYPE_ENUM,
  INTEGRITY_TYPE_SELECTOR,
} from 'features/settings/integrityManagement/helpers/integrityManagement.constants';

const getSubRows = (row) => {
  const subRows = [...(row.categories || []), ...(row.details || [])];

  // Check if any item in subRows also contains nested categories or details
  subRows.forEach((subRow) => {
    subRow.subRows = getSubRows(subRow);
  });

  const picklistSubRow = subRows.filter(
    (row) =>
      (row.score !== null && row.score !== undefined) &&
      row.integrityDetailType === RULE_TYPES_ENUM.RULE_PICKLIST,
  );

  return !picklistSubRow.length && subRows.length > 0 ? subRows : undefined;
};

const IntegritiesTable = ({
  integrities,
  deleteIntegrity,
  riskPrincipleType,
  classes,
}) => {
  const columns = useMemo(
    () => [
      {
        id: 'expander',
        xs: 2,
        disableSortBy: true,
        Header: <TableRowActionsCell minItems={1} />,
        Cell: ({ row }) => {
          if (!row.canExpand) return <TableRowActionsCell minItems={1} />;

          return (
            <TableRowActionsCell
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 2}rem`,
                },
              })}
            >
              <IconButton>
                {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </TableRowActionsCell>
          );
        },
      },
      {
        xs: 4,
        Header: 'Name',
        accessor: (integrity) => integrity.title,
        Cell: ({ row, value }) => {
          const sumWeights = (items = []) => items.reduce((sum, { weight = 0 } = {}) => sum + weight, 0);

          const { details, categories } = row.original || {};
          const totalWeight = sumWeights(details) + sumWeights(categories);
          const isCategory = row.original?.integrityType === INTEGRITY_TYPE_SELECTOR.CATEGORY;
          const warningText = `The total weight must equal 100. The current sum is ${totalWeight}.`;

          return (
            <Typography
              variant="subtitle2"
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 2}rem`,
                },
              })}
            >
              {value}
              {totalWeight !== 100 && isCategory && (
                <WarningIcon
                  className={classes.warningIcon}
                  titleAccess={warningText}
                />
              )}
            </Typography>
          );
        },
      },
      {
        xs: 2,
        Header: 'Weight',
        accessor: (integrity) => integrity.weight,
        Cell: ({ row }) => {
          const integrity = row.original;

          if (!integrity) {
            return null;
          }

          return (
            <Grid item xs={2}>
              <Typography variant="body2">
                {`${integrity.weight}%` || '-'}
              </Typography>
            </Grid>
          );
        },
      },
      {
        xs: 3,
        Header: 'Type',
        accessor: (integrity) => integrity.integrityType,
        Cell: ({ row }) => {
          const integrity = row.original;

          // only rules have integrityDetailType
          if (!integrity || !integrity.integrityDetailType) {
            return null;
          }

          return (
            <Grid item xs={2}>
              <Typography variant="body2">
                {getRuleTypeString(integrity.integrityDetailType) || '-'}
              </Typography>
            </Grid>
          );
        },
      },
      {
        xs: 1,
        id: 'edit',
        Cell: ({ row }) => {
          const integrity = row.original;

          if (
            !integrity &&
            (!integrity.integrityType || !integrity.integrityRuleType)
          ) {
            return null;
          }

          if (
            integrity.integrityType &&
            integrity.integrityType === INTEGRITY_TYPE_ENUM.RISK_PRINCIPLE
          ) {
            return null;
          }

          return (
            <Grid item>
              <Link
                to={{
                  pathname: routePaths.settings.integrity.new,
                  state: {
                    riskPrincipleType: riskPrincipleType,
                    editIntegrity: integrity,
                  },
                }}
                style={{ textDecoration: 'none' }}
              >
                <IconButton color="default">
                  <EditIcon />
                </IconButton>
              </Link>
            </Grid>
          );
        },
      },
      {
        xs: 1,
        id: 'delete',
        Cell: ({ row }) => {
          const integrity = row.original;

          if (
            !integrity &&
            (!integrity.integrityType || !integrity.integrityRuleType)
          ) {
            return null;
          }

          if (
            integrity.integrityType &&
            integrity.integrityType === INTEGRITY_TYPE_ENUM.RISK_PRINCIPLE
          ) {
            return null;
          }

          // when calling deleteIntegrity check if there are any underlying categories and or rules - create a popup as well

          return (
            <Grid item>
              <IconButton
                color="default"
                onClick={() =>
                  deleteIntegrity(
                    integrity.integrityId,
                    integrity.integrityType,
                  )
                }
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [deleteIntegrity],
  );

  const buildExpandedState = (items, expanded = {}, parentIndex = '') => {
    items.forEach((item, index) => {
      const rowIndex = parentIndex ? `${parentIndex}.${index}` : `${index}`;
      expanded[rowIndex] = true;

      const subRows = getSubRows(item);
      if (subRows) {
        buildExpandedState(subRows, expanded, rowIndex);
      }
    });
    return expanded;
  };

  const initialState = useMemo(() => {
    const expandedRows = buildExpandedState(integrities || []);
    return { expanded: expandedRows };
    // eslint-disable-next-line
  }, [integrities]);

  return (
    <Table
      useExpanded
      subrowOffset={3}
      getSubRows={getSubRows}
      items={integrities ?? EMPTY_LIST}
      columns={columns}
      useGlobalFilter={false}
      noItemsMessage="There are no rules for this organization."
      initialState={initialState} // Added the initialState prop to set expanded rows
    />
  );
};

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.grey[700],
  },
  warningIcon: {
    color: 'orange',
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
    verticalAlign: 'middle',
  },
});

export default withStyles(styles)(IntegritiesTable);
